'use client'

import type { ExperimentWithStats } from '../types'
import { Eye, Pause, Play } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { dictionary } from '@/utils/dictionary'
import { useAuth } from '@/context/AuthContext'

interface ABTestsTableProps {
  experiments: ExperimentWithStats[]
  onView: (experiment: ExperimentWithStats) => void
  onTogglePause: (experimentId: string) => void
}

export function ABTestsTable({ experiments, onView, onTogglePause }: ABTestsTableProps) {
  const { language } = useAuth()

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const GRID_COLS =
    'grid-cols-[2fr_1fr_1.2fr_1fr_1fr_1fr_1fr_1fr]'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="grid grid-cols-[2fr_1fr_1.2fr_1fr_1.2fr_1fr_1fr_1fr] gap-4 px-6 py-4 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div>{dictionary['test'][language]}</div>
        <div>{dictionary['status'][language]}</div>
        <div>{dictionary['variants'][language]}</div>
        <div className="text-center">{dictionary['visits'][language]}</div>
        <div className="text-center">{dictionary['conversions'][language]}</div>
        <div className="text-center">{dictionary['conversion-rate'][language]}</div>
        <div className="text-center">{dictionary['revenue'][language]}</div>
        <div className="text-center">{dictionary['actions'][language]}</div>
      </div>

      {experiments.map((experiment) => (
        <div
          key={experiment.id}
          className={`grid ${GRID_COLS} items-center px-6 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors`}
        >
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">
              {experiment.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {dictionary['id'][language]}: {experiment.id} ·{' '}
              {dictionary['base-price'][language]}:{' '}
              {formatCurrency(experiment.base_price)}
            </p>
          </div>

          <div className="flex justify-center">
            {experiment.status === 'active' ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                <Play className="w-3 h-3 fill-current" />
                {dictionary['active'][language]}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                <Pause className="w-3 h-3" />
                {dictionary['paused'][language]}
              </span>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900">
              {experiment.variants.length} {dictionary['variants'][language]}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {experiment.variants.map(v => formatCurrency(v.price)).join(', ')}
            </p>
          </div>

          <div className="flex justify-center text-sm font-medium text-gray-900">
            {formatNumber(experiment.stats?.visits ?? 0)}
          </div>

          <div className="flex justify-center text-sm font-medium text-gray-900">
            {formatNumber(experiment.stats?.conversions ?? 0)}
          </div>

          <div
            className={`flex justify-center text-sm font-semibold
              ${((experiment.stats?.conversion_rate ?? 0) >= 5)
                ? 'text-green-600'
                : 'text-red-500'}`}
          >
            {(experiment.stats?.conversion_rate ?? 0).toFixed(2)}%
          </div>

          <div className="flex justify-center text-sm font-semibold text-gray-900">
            {formatCurrency(experiment.stats?.revenue ?? 0)}
          </div>

          <div className="flex justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onView(experiment)}
            >
              <Eye className="h-4 w-4 text-blue-500" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onTogglePause(experiment.id.toString())}
            >
              {experiment.status === 'active'
                ? <Pause className="h-4 w-4 text-[#4B5563] " />
                : <Play className="h-4 w-4 fill-[#F0FDF4] text-[#16803C]" />}
            </Button>
          </div>
        </div>
      ))}

      {experiments.length === 0 && (
        <div className="px-6 py-4 text-center text-gray-500">
          {dictionary['no-ab-tests-found'][language]}
        </div>
      )}
    </div>
  )
}
